import { useEffect, useState } from 'react';
import type { Page } from '../types/Page';
import { getPages } from '../api/pages';

// Module-level cache. Pages are returned permission-filtered by the backend,
// so a single fetch per session is sufficient. Cleared on logout via
// clearPagesCache() — next mount re-fetches.
let cache: Page[] | null = null;
let inflight: Promise<Page[]> | null = null;

function loadPages(): Promise<Page[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getPages()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearPagesCache(): void {
  cache = null;
  inflight = null;
}

interface UsePagesResult {
  pages: Page[] | null;
  loading: boolean;
  error: string | null;
}

export function usePages(): UsePagesResult {
  const [state, setState] = useState<UsePagesResult>(() =>
    cache
      ? { pages: cache, loading: false, error: null }
      : { pages: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.pages === cache
          ? prev
          : { pages: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadPages()
      .then((data) => {
        if (cancelled) return;
        setState({ pages: data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ pages: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
